<script setup lang="ts" generic="T extends { lat: number; lng: number }">
import { computed, inject, toRaw, useTemplateRef, watch } from "vue";
import { CustomMarker } from "vue3-google-map";

import { hasSpiderfyOffset, type IMapMarkerOffset, SPIDERFY_MARKER_Z_INDEX } from "@/components/ui/map/map-spiderfy";
import { mapSpiderfyMarkerRegistryKey } from "@/components/ui/map/map-spiderfy-context";

const DEFAULT_MARKER_Z_INDEX = 1;

interface IProps {
  marker: T;
  offset?: IMapMarkerOffset;
  visible?: boolean;
}

interface IEmits {
  (e: "click", payload: T): void;
}

const props = defineProps<IProps>();
defineEmits<IEmits>();

const customMarkerComponent = useTemplateRef<InstanceType<typeof CustomMarker>>("customMarkerComponent");
const markerElement = useTemplateRef<HTMLElement>("markerElement");
const markerRegistry = inject(mapSpiderfyMarkerRegistryKey, undefined);
let cachedMarkerWidth = 0;

const markerOptions = computed(() => ({
  position: { lat: props.marker.lat, lng: props.marker.lng },
  anchorPoint: "TOP_CENTER" as const,
}));

const markerTransform = computed(() => {
  const { x = 0, y = 0 } = props.offset ?? {};
  return `translate(${x}px, ${y}px)`;
});

const markerZIndex = computed(() => {
  return hasSpiderfyOffset(props.offset) ? SPIDERFY_MARKER_Z_INDEX : DEFAULT_MARKER_Z_INDEX;
});

function getMarkerWidth() {
  const renderedWidth = markerElement.value?.getBoundingClientRect().width ?? 0;
  if (renderedWidth > 0) {
    cachedMarkerWidth = renderedWidth;
  }
  return cachedMarkerWidth;
}

watch(
  () => customMarkerComponent.value?.customMarker,
  (customMarker, _, onCleanup) => {
    if (!customMarker) {
      return;
    }

    getMarkerWidth();
    markerRegistry?.register(toRaw(props.marker), customMarker, getMarkerWidth);
    onCleanup(() => markerRegistry?.unregister(customMarker));
  },
  { immediate: true, flush: "post" },
);
</script>

<template>
  <CustomMarker
    ref="customMarkerComponent"
    :options="markerOptions"
    :style="{ zIndex: markerZIndex }"
  >
    <div
      v-show="props.visible !== false"
      ref="markerElement"
      role="button"
      :style="{ textAlign: 'center', transform: markerTransform }"
      @click.stop="$emit('click', props.marker)"
    >
      <slot />
    </div>
  </CustomMarker>
</template>
